/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import {
  DEFAULT_INDUSTRY_TYPE,
  INDUSTRY_TYPES,
  type IndustryType,
} from "./industries";

export interface IndustryTerminology {
  project: string;
  workArea: string;
  phase: string;
  task: string;
  estimate: string;
  lineItem: string;
  margin: string;
  overhead: string;
  material: string;
  labor: string;
  equipment: string;
  client: string;
  lead: string;
  contact: string;
  takeoff: string;
  proposal: string;
  contract: string;
  activityVerb: string;
  completionNoun: string;
  customTerms: Record<string, string>;
}

const baseTerminology: IndustryTerminology = {
  project: "Project",
  workArea: "Work Area",
  phase: "Phase",
  task: "Task",
  estimate: "Estimate",
  lineItem: "Line Item",
  margin: "Margin",
  overhead: "Overhead",
  material: "Material",
  labor: "Labor",
  equipment: "Equipment",
  client: "Client",
  lead: "Lead",
  contact: "Contact",
  takeoff: "Takeoff",
  proposal: "Proposal",
  contract: "Contract",
  activityVerb: "Build",
  completionNoun: "Completion",
  customTerms: {},
};

export const INDUSTRY_TERMINOLOGY: Record<IndustryType, IndustryTerminology> = {
  [INDUSTRY_TYPES.GENERAL_CONTRACTOR]: {
    ...baseTerminology,
    overhead: "General Conditions",
  },
  [INDUSTRY_TYPES.RESIDENTIAL_CONSTRUCTION]: {
    ...baseTerminology,
    project: "Build",
    workArea: "Room",
    estimate: "Quote",
    client: "Homeowner",
    activityVerb: "Build",
    completionNoun: "Handover",
  },
  [INDUSTRY_TYPES.ELECTRICAL_CONTRACTOR]: {
    ...baseTerminology,
    workArea: "Circuit",
    task: "Task",
    activityVerb: "Install",
    completionNoun: "Commissioning",
  },
  [INDUSTRY_TYPES.PLUMBING_CONTRACTOR]: {
    ...baseTerminology,
    workArea: "System",
    task: "Work Item",
    activityVerb: "Install",
  },
  [INDUSTRY_TYPES.HVAC_CONTRACTOR]: {
    ...baseTerminology,
    workArea: "Zone",
    equipment: "Equipment",
    activityVerb: "Service",
  },
  [INDUSTRY_TYPES.ROOFING_CONTRACTOR]: {
    ...baseTerminology,
    workArea: "Roof Area",
    task: "Work Section",
    activityVerb: "Install",
    completionNoun: "Delivery",
  },
  [INDUSTRY_TYPES.CONCRETE_CONTRACTOR]: {
    ...baseTerminology,
    workArea: "Pour",
    task: "Placement",
    activityVerb: "Pour",
  },
  [INDUSTRY_TYPES.LANDSCAPING]: {
    ...baseTerminology,
    project: "Engagement",
    workArea: "Site Zone",
    material: "Supply",
    labor: "Crew",
    activityVerb: "Maintain",
    completionNoun: "Service",
  },
};

export function getIndustryTerminology(
  industry: IndustryType | null | undefined,
): IndustryTerminology {
  if (!industry) {
    return INDUSTRY_TERMINOLOGY[DEFAULT_INDUSTRY_TYPE];
  }
  return (
    INDUSTRY_TERMINOLOGY[industry] ??
    INDUSTRY_TERMINOLOGY[DEFAULT_INDUSTRY_TYPE]
  );
}
